// Un identifiant provisoire ne tenait qu'a quatre caracteres tires au hasard :
// dans une meme milliseconde — un ajout en lot, par exemple — deux lignes
// pouvaient recevoir le meme, et l'ecran les confondait. Un compteur qui ne
// recule jamais l'ecarte definitivement pour la session ; l'horodatage et le
// hasard distinguent les sessions entre elles.
let suiteUid = 0;

/**
 * Identifiant provisoire, unique pour la session en cours.
 *
 * @returns {string} un identifiant que la synchronisation tiendra pour nouveau
 */
export const uid = () =>
  Date.now().toString(36) +
  (suiteUid += 1).toString(36).padStart(3, '0') +
  Math.random().toString(36).slice(2, 6);

export const today = () =>
  new Date().toLocaleDateString('fr-CH', { day: '2-digit', month: '2-digit', year: 'numeric' });

/**
 * La date du jour à l'heure locale, au format `AAAA-MM-JJ`.
 *
 * <p>`toISOString()` donne la date de Greenwich : entre minuit et 2 h en été,
 * un pointage ou la feuille de présence tombait sur la veille.
 *
 * @returns {string} la date du jour
 */
export const aujourdhuiIso = () => {
  const d = new Date();
  return (
    d.getFullYear() +
    '-' +
    String(d.getMonth() + 1).padStart(2, '0') +
    '-' +
    String(d.getDate()).padStart(2, '0')
  );
};

export const hours = (e, s, p = 0) => {
  if (!e || !s) return 0;
  return Math.max(
    0,
    (new Date('2000-01-01T' + s) - new Date('2000-01-01T' + e)) / 3600000 - p / 60,
  );
};

export const fillTpl = (tpl, v) =>
  String(tpl || '')
    .replace(/\{client\}/g, v.client || '')
    .replace(/\{num\}/g, v.num || '')
    .replace(/\{lien\}/g, v.lien || '')
    .replace(/\{societe\}/g, v.societe || '');

/**
 * Numéro d'une pièce, enregistrée ou non.
 *
 * <p>Une pièce que le serveur n'a pas encore vue n'a pas de champ `num` : son
 * numéro est son identifiant provisoire, et c'est lui qui part au serveur. La
 * référence de paiement se calculait sur `num` seul : une facture neuve
 * affichait « RF04 0 » au lieu de « RF74 01 » jusqu'à son enregistrement.
 *
 * @param {object} piece offre ou facture
 * @returns {string} son numéro, vide si elle n'en a pas
 */
export const numeroDe = (piece) =>
  String(piece?.num || (typeof piece?.id === 'string' ? piece.id : '') || '');

/**
 * Vrai quand une autre pièce de la liste porte déjà ce numéro.
 *
 * @param {object[]} pieces offres ou factures
 * @param {string} numero numéro voulu
 * @param {string|number} [saufId] la pièce elle-même, quand on la modifie
 * @returns {boolean} le numéro est pris
 */
export const numeroPris = (pieces, numero, saufId) =>
  (pieces || []).some((x) => x.id !== saufId && numeroDe(x).trim() === String(numero || '').trim());

/**
 * Prochain numéro d'une série de pièces (offres, factures) : « 01 », « 02 »…
 *
 * <p>Il se calculait sur l'identifiant. Or, une fois la pièce enregistrée,
 * l'identifiant est celui de la base, commun à toutes les sociétés : la
 * deuxième offre d'une société récente prenait le numéro « 06 », et la série
 * se trouait d'autant que les autres sociétés avaient travaillé. Le rang se lit
 * désormais sur le numéro, comme le fait le serveur : seuls comptent les
 * numéros purement numériques ; une pièce pas encore enregistrée porte son
 * numéro dans son identifiant provisoire.
 *
 * @param {object[]} arr pièces de la société
 * @returns {string} le numéro suivant, sur au moins deux chiffres
 */
export const nextSeq = (arr) => {
  const rang = (x) => {
    const numero = x?.num || (typeof x?.id === 'string' ? x.id : '');
    return /^\d+$/.test(String(numero)) ? parseInt(numero, 10) : 0;
  };
  return String((arr || []).reduce((m, x) => Math.max(m, rang(x)), 0) + 1).padStart(2, '0');
};

/**
 * Les éléments dans l'ordre où ils ont été saisis, comme la maquette les
 * présente (clients…) : les enregistrés par identifiant croissant, puis ceux
 * qui attendent encore leur identifiant serveur, dans leur ordre.
 *
 * @param {object[]} arr éléments
 * @returns {object[]} une copie triée
 */
export const ordreDeSaisie = (arr) =>
  (arr || [])
    .map((x, i) => [x, i])
    .sort(([a, i], [b, j]) => {
      const na = typeof a?.id === 'number';
      const nb = typeof b?.id === 'number';
      if (na && nb) return a.id - b.id;
      if (na !== nb) return na ? -1 : 1;
      return i - j;
    })
    .map(([x]) => x);

/**
 * L'inverse : la dernière pièce créée en tête, comme les listes de devis et de
 * factures de la maquette (chaque nouvelle pièce s'y ajoute au début).
 *
 * @param {object[]} arr éléments
 * @returns {object[]} une copie triée
 */
export const plusRecentsDabord = (arr) => ordreDeSaisie(arr).reverse();

/** Jour d'une pièce (« 18.10.2026 » ou « 2026-10-18 »), en nombre comparable. */
const jourDe = (piece) => {
  const t = String(piece?.date || '');
  const fr = t.match(/(\d{1,2})[./](\d{1,2})[./](\d{2,4})/);
  if (fr) {
    const an = +fr[3] < 100 ? +fr[3] + 2000 : +fr[3];
    return an * 10000 + +fr[2] * 100 + +fr[1];
  }
  const iso = t.match(/(\d{4})-(\d{2})-(\d{2})/);
  return iso ? +iso[1] * 10000 + +iso[2] * 100 + +iso[3] : 0;
};

/**
 * Les offres et factures par numéro, le plus grand en tête : « c'est pas bien
 * classé par rapport au numéro ». Une offre n° 48 datée de mai passait sous la
 * n° 43 de septembre. Les numéros « 2026-017 » se comparent année puis rang ;
 * une pièce sans numéro suit, par date puis par ordre de saisie.
 *
 * @param {object[]} arr offres ou factures
 * @returns {object[]} une copie triée, le plus grand numéro en tête
 */
export const plusRecentesEnTete = (arr) => {
  const saisie = plusRecentsDabord(arr);
  const rang = new Map(saisie.map((x, i) => [x, i]));
  const morceaux = (x) => (String(numeroDe(x) || '').match(/\d+/g) || []).map(Number);
  const parNumero = (a, b) => {
    const na = morceaux(a);
    const nb = morceaux(b);
    if (!na.length || !nb.length) return nb.length - na.length;
    for (let i = 0; i < Math.max(na.length, nb.length); i++) {
      const d = (nb[i] ?? -1) - (na[i] ?? -1);
      if (d) return d;
    }
    return 0;
  };
  return [...saisie].sort(
    (a, b) => parNumero(a, b) || jourDe(b) - jourDe(a) || rang.get(a) - rang.get(b),
  );
};
